import React, { useEffect, useState } from "react";
import { Loader2, Sparkles, X, ArrowRight, Check } from "lucide-react";
import api from "../../lib/api";

/**
 * Global trial / plan-limit modal.
 *
 * Listens for the custom DOM event `compliche:trial-limit` which the axios
 * interceptor dispatches every time the backend returns HTTP 402.
 */
const LIMIT_BLURBS = {
  clients:             "You've reached your active client limit on this plan.",
  scans:               "You've used all the compliance checks on this plan.",
  team:                "You've reached the team-member limit on this plan.",
  audit_export:        "Audit trail export is not available on your current plan.",
  identity_verification: "Identity verification requires Practitioner or higher.",
  aml_screenings:      "You've used all the AML screenings on this plan.",
  api_access:          "API access requires Professional or higher.",
};

const PRICE_LABEL = {
  starter:      "Starter · £49/mo",
  practitioner: "Practitioner · £99/mo",
  professional: "Professional · £199/mo",
  enterprise:   "Enterprise · £499/mo",
};

const RECOMMENDED = {
  clients: "practitioner",
  scans: "practitioner",
  team: "practitioner",
  audit_export: "practitioner",
  identity_verification: "practitioner",
  aml_screenings: "practitioner",
  api_access: "professional",
};

export default function TrialLimitModal() {
  const [payload, setPayload] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    const handler = (e) => setPayload(e.detail || {});
    window.addEventListener("compliche:trial-limit", handler);
    return () => window.removeEventListener("compliche:trial-limit", handler);
  }, []);

  if (!payload) return null;

  const reason = LIMIT_BLURBS[payload.limit] || payload.message || "You've reached a plan limit.";
  const recommended = RECOMMENDED[payload.limit] || "practitioner";

  const startCheckout = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/api/billing/checkout-session", {
        plan, billing_period: "monthly", origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else alert(data?.message || "Unable to start checkout. Please contact hello@compliche.com.");
    } catch (e) {
      alert("Could not start checkout. Please contact hello@compliche.com.");
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4" onClick={() => !busy && setPayload(null)} data-testid="trial-limit-modal">
      <div className="card2 max-w-lg w-full p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-navy-900 text-white px-6 py-5 flex items-start gap-3">
          <Sparkles size={20} className="text-emerald2 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="t-h3 text-white">Upgrade to continue</div>
            <p className="text-white/70 t-body-sm mt-1">{reason}</p>
          </div>
          <button onClick={() => setPayload(null)} className="text-white/60 hover:text-white" disabled={!!busy}><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          <p className="t-body text-slate2">Compliche plans start from <strong className="text-ink">£49/month</strong>. 14-day free trial included. Cancel anytime.</p>

          <div className="space-y-2">
            {["starter", "practitioner", "professional", "enterprise"].map((p) => (
              <button key={p} onClick={() => startCheckout(p)} disabled={!!busy}
                className={`w-full btn ${p === recommended ? "btn-primary" : "btn-secondary"} btn-lg justify-between`}
                data-testid={`upgrade-${p}`}>
                <span className="flex items-center gap-2">
                  {p === recommended && <Check size={14} />}
                  {PRICE_LABEL[p]}
                </span>
                {busy === p ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              </button>
            ))}
          </div>

          <p className="t-caption text-center">All plans include 14 days free. Card required at signup — not charged until day 15.</p>
        </div>
      </div>
    </div>
  );
}
